import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './ResubmitProposal.css';

const ResubmitProposal = () => {
  const { proposalId } = useParams();
  const [proposal, setProposal] = useState(null);
  const [updatedFiles, setUpdatedFiles] = useState({});
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const fileInputs = useRef({});

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/proposals/details/${proposalId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        if (response.ok) {
          const data = await response.json();
          setProposal(data);
        } else {
          console.error('Failed to fetch proposal details');
        }
      } catch (error) {
        console.error('Error fetching proposal details:', error);
      }
    };

    fetchProposal();
  }, [API_BASE_URL, proposalId]);

  const handleFileChange = (documentId, file) => {
    setUpdatedFiles((prev) => ({ ...prev, [documentId]: file }));
    setIsUpdateEnabled(true);
  };

  const handleClearFile = (documentId) => {
    setUpdatedFiles((prev) => {
      const updated = { ...prev };
      delete updated[documentId];
      return updated;
    });
    setIsUpdateEnabled(true);
    if (fileInputs.current[documentId]) {
      fileInputs.current[documentId].value = '';
    }
  };

  const handleResubmit = async () => {
    const formData = new FormData();
    Object.entries(updatedFiles).forEach(([documentId, file]) => {
      formData.append(documentId, file);
    });

    try {
      const response = await fetch(`${API_BASE_URL}/resubmit-submissions/${proposalId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });

      if (response.ok) {
        alert('Documents resubmitted successfully!');
        setUpdatedFiles({});
        setIsUpdateEnabled(false);
        window.location.reload();
      } else {
        alert('Failed to resubmit documents.');
      }
    } catch (error) {
      console.error('Error during resubmission:', error);
    }
  };

  if (!proposal) return <div>Loading...</div>;

  const visibleDocs = (proposal.documents || []).filter(
    (d) => String(d.status || '').toLowerCase() === 'resubmit'
  );

  return (
    <div className="resubmit-container">
      <h2 className="proposal-title">Proposal Name: {proposal.proposal.title}</h2>
      <h3 className="section-title">Proposal ID : {proposal.proposal.custom_id}</h3>

      {visibleDocs.length === 0 ? (
        <div className="no-resubmit-note">No documents currently require resubmission.</div>
      ) : (
        visibleDocs.map((doc) => {
          const statusLower = String(doc.status || '').toLowerCase();
          const isResubmit = statusLower === 'resubmit';

          return (
            <div key={doc.document_id} className="document-item">
              <div className="document-row">
                <div className="document-header">
                  <div className="document-name">{doc.document_name}</div>
                  <div className="status-container">
                    <span className="status-label">Status:</span>
                    <span
                      className={`status-badge ${
                        doc.status === 'resubmitted' ? 'green' :
                        isResubmit ? 'orange' : 'gray'
                      }`}
                    >
                      {doc.status || 'None'}
                    </span>
                  </div>
                </div>

                <div className="document-body">
                  <div className="document-comment">
                    <strong>Comment:</strong>
                    <div>{doc.comment || '—'}</div>
                  </div>

                  <div className="upload-box">
                    <input
                      type="file"
                      accept=".pdf"
                      className="file-input"
                      disabled={!isResubmit}
                      onChange={(e) => handleFileChange(doc.document_id, e.target.files[0])}
                      ref={(el) => (fileInputs.current[doc.document_id] = el)}
                    />
                    {updatedFiles[doc.document_id] && (
                      <button
                        className="clear-file-button"
                        onClick={() => handleClearFile(doc.document_id)}
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      <button
        className={`update-button ${isUpdateEnabled ? '' : 'disabled'}`}
        onClick={handleResubmit}
        disabled={!isUpdateEnabled}
      >
        Resubmit Documents
      </button>
    </div>
  );
};

export default ResubmitProposal;
